import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mail, Phone, Sparkles, Clock, Building2, CheckCircle2, Download, Link as LinkIcon, FileText, CalendarClock, BarChart3, TrendingUp } from "lucide-react";

const MARKETING_PDFS = [
  {
    key: "facility-recruit",
    title: "Facility Recruitment Flyer",
    subtitle: "1-page, call-driven — share with AFCs, assisted livings, group homes, adult day centers ($299/yr Zelle)",
    file: "/providence-partners-facility-membership.pdf",
  },
  {
    key: "info-sheet",
    title: "Families Seeking Care Info Sheet",
    subtitle: "1-page, call-driven — share with working caregivers, hospital discharge planners, social workers (free placement help)",
    file: "/providence-partners-info-sheet.pdf",
  },
  {
    key: "business-card",
    title: "Business Card (single, 3.5×2)",
    subtitle: "Standard business-card size — send to a print vendor (Vistaprint, Staples, etc.)",
    file: "/providence-partners-business-card.pdf",
  },
  {
    key: "business-card-sheet",
    title: "Business Cards — 10-up print sheet",
    subtitle: "US Letter, 10 cards (2×5) — print at home and cut along guides",
    file: "/providence-partners-business-card-sheet.pdf",
  },
];

const PRIORITY_LABEL = { memory_care: "Memory care / dementia", parkinsons: "Parkinson's", mobility: "Mobility / stroke", general: "General" };
const TIMELINE_LABEL = { urgent: "Urgent (≤ 2 wks)", "1_3_months": "1–3 months", exploring: "Exploring" };

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [callRequests, setCallRequests] = useState([]);
  const [leadStats, setLeadStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: pl }, { data: fac }, { data: calls }, { data: stats }] = await Promise.all([
        api.get("/admin/placement-requests"),
        api.get("/admin/facilities"),
        api.get("/admin/facility-call-requests"),
        api.get("/admin/lead-stats"),
      ]);
      setItems(pl.items || []);
      setFacilities(fac.items || []);
      setCallRequests(calls.items || []);
      setLeadStats(stats);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateCallStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/facility-call-requests/${id}`, { status });
      setCallRequests((arr) => arr.map((i) => (i.id === id ? data : i)));
    } catch { /* noop */ }
  };

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/placement-requests/${id}`, { status });
      setItems((arr) => arr.map((i) => (i.id === id ? data : i)));
    } catch { /* noop */ }
  };

  const activateMembership = async (fid) => {
    setActivating(fid);
    try {
      const { data } = await api.post(`/admin/facilities/${fid}/activate-membership`);
      setFacilities((arr) => arr.map((f) => (f.id === fid ? data : f)));
      toast.success("Membership activated — 365 days from today");
    } catch { toast.error("Could not activate membership"); }
    finally { setActivating(null); }
  };

  const deleteFacility = async (fid, name) => {
    if (!window.confirm(`Permanently delete "${name}"?\n\nThis removes the facility listing and all its inquiries. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/facilities/${fid}`);
      setFacilities((arr) => arr.filter((f) => f.id !== fid));
      toast.success(`"${name}" removed`);
    } catch { toast.error("Could not delete facility"); }
  };

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-6xl px-6 py-10 lg:px-8">
      <p className="text-sm uppercase tracking-[0.18em] text-primary">
        <Sparkles className="mb-1 mr-1 inline h-3.5 w-3.5" /> Providence Partners admin
      </p>
      <h1 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">Admin dashboard</h1>
      <p className="mt-2 text-muted-foreground">Placement leads and member-facility memberships in one place.</p>

      <section className="mt-10" data-testid="marketing-materials-section">
        <h2 className="font-serif text-2xl text-forest"><FileText className="mr-1 inline h-5 w-5 text-gold" /> Marketing materials</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Download a printable PDF or copy a share link to text/email potential member facilities and referral partners.
        </p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {MARKETING_PDFS.map((pdf) => {
            const shareUrl = `${window.location.origin}${pdf.file}`;
            return (
              <Card key={pdf.key} data-testid={`marketing-${pdf.key}`} className="rounded-2xl border border-border/60 bg-card p-5">
                <h3 className="font-serif text-lg text-forest">{pdf.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{pdf.subtitle}</p>
                <p className="mt-3 break-all rounded-lg bg-muted/50 px-3 py-2 font-mono text-[11px] text-muted-foreground" data-testid={`marketing-${pdf.key}-url`}>{shareUrl}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={pdf.file} target="_blank" rel="noopener noreferrer" download data-testid={`marketing-${pdf.key}-download`}>
                    <Button size="sm" className="rounded-full bg-primary text-primary-foreground">
                      <Download className="mr-1.5 h-3.5 w-3.5" /> Download PDF
                    </Button>
                  </a>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(shareUrl);
                        toast.success("Share link copied — paste it into a text or email");
                      } catch {
                        toast.error("Could not copy. Long-press the URL above to copy.");
                      }
                    }}
                    data-testid={`marketing-${pdf.key}-copy`}
                    className="rounded-full"
                  >
                    <LinkIcon className="mr-1.5 h-3.5 w-3.5" /> Copy share link
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Lead source stats */}
      {leadStats && (
        <section className="mt-10" data-testid="lead-stats-section">
          <h2 className="font-serif text-2xl text-forest"><BarChart3 className="mr-1 inline h-5 w-5 text-gold" /> Where your leads come from</h2>
          <p className="mt-1 text-sm text-muted-foreground">See which channel drives the most families — so you can double down on what's working.</p>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <Card data-testid="stats-total-week" className="rounded-2xl border border-emerald/30 bg-gradient-to-br from-emerald/8 to-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald">
                <TrendingUp className="h-4 w-4" /> Total this week
              </div>
              <p className="mt-2 font-serif text-4xl text-forest">{leadStats.total_this_week}</p>
              <p className="mt-1 text-xs text-muted-foreground">Across all channels · Last 7 days</p>
            </Card>
            <Card data-testid="stats-total-alltime" className="rounded-2xl border border-border/60 bg-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">All time</div>
              <p className="mt-2 font-serif text-4xl text-forest">{leadStats.total_all_time}</p>
              <p className="mt-1 text-xs text-muted-foreground">Since launch</p>
            </Card>
            <Card data-testid="stats-followup-week" className="rounded-2xl border border-gold/40 bg-gold/5 p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold">To follow up</div>
              <p className="mt-2 font-serif text-4xl text-forest">
                {items.filter((i) => (i.status || "new") === "new").length + callRequests.filter((c) => (c.status || "new") === "new").length}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Leads still marked "new"</p>
            </Card>
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Card data-testid="stats-placement-by-source" className="rounded-2xl border border-border/60 bg-card p-5">
              <h3 className="font-serif text-lg text-forest">Placement leads by source</h3>
              <p className="mt-1 text-xs text-muted-foreground">This week / all time</p>
              <div className="mt-3 space-y-2">
                {Object.keys({ ...leadStats.placement_requests.this_week, ...leadStats.placement_requests.all_time }).length === 0 && (
                  <p className="text-sm text-muted-foreground">No placement leads yet.</p>
                )}
                {Object.keys({ ...leadStats.placement_requests.this_week, ...leadStats.placement_requests.all_time }).sort().map((src) => (
                  <div key={src} className="flex items-center justify-between border-b border-border/50 pb-1.5 text-sm">
                    <span className="text-foreground">{src}</span>
                    <span className="font-mono text-muted-foreground">
                      <span className="font-semibold text-forest">{leadStats.placement_requests.this_week[src] || 0}</span> / {leadStats.placement_requests.all_time[src] || 0}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            <Card data-testid="stats-other-leads" className="rounded-2xl border border-border/60 bg-card p-5">
              <h3 className="font-serif text-lg text-forest">Other lead channels</h3>
              <p className="mt-1 text-xs text-muted-foreground">This week / all time</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center justify-between border-b border-border/50 pb-1.5">
                  <span className="text-foreground">15-min call requests (facilities)</span>
                  <span className="font-mono text-muted-foreground">
                    <span className="font-semibold text-forest">{leadStats.facility_call_requests.this_week}</span> / {leadStats.facility_call_requests.all_time}
                  </span>
                </div>
                <div className="flex items-center justify-between border-b border-border/50 pb-1.5">
                  <span className="text-foreground">Facility inquiries (caregivers)</span>
                  <span className="font-mono text-muted-foreground">
                    <span className="font-semibold text-forest">{leadStats.facility_inquiries.this_week}</span> / {leadStats.facility_inquiries.all_time}
                  </span>
                </div>
              </div>
              <p className="mt-4 text-xs italic text-muted-foreground">
                Tip: leads tagged "Homepage widget" come from the "Get free help" button on the landing page.
              </p>
            </Card>
          </div>
        </section>
      )}

      {/* Facility 15-min call requests */}
      <section className="mt-12" data-testid="facility-calls-section">
        <h2 className="font-serif text-2xl text-forest"><CalendarClock className="mr-1 inline h-5 w-5 text-gold" /> 15-min call requests (from facilities)</h2>
        <p className="mt-1 text-sm text-muted-foreground">Facility owners who clicked "Schedule a 15-min call" on the public /for-facilities page.</p>
        <div className="mt-5 grid gap-3">
          {callRequests.length === 0 && (
            <Card className="rounded-2xl border border-dashed border-border p-6 text-center text-muted-foreground">No call requests yet.</Card>
          )}
          {callRequests.map((c) => (
            <Card key={c.id} data-testid={`call-request-${c.id}`} className="rounded-2xl border border-border/60 bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-xl text-forest">{c.contact_name}{c.facility_name ? ` · ${c.facility_name}` : ""}</h3>
                  <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    <a href={`tel:${c.contact_phone}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`call-phone-${c.id}`}><Phone className="h-3.5 w-3.5" /> {c.contact_phone}</a>
                    {c.contact_email && <a href={`mailto:${c.contact_email}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`call-email-${c.id}`}><Mail className="h-3.5 w-3.5" /> {c.contact_email}</a>}
                  </p>
                  {c.best_time && <p className="mt-1.5 text-sm text-foreground"><span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Best time:</span> {c.best_time}</p>}
                  {c.notes && <p className="mt-1 text-sm text-foreground"><span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes:</span> {c.notes}</p>}
                </div>
                <Badge variant="outline" className="rounded-full text-xs capitalize">{c.status}</Badge>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 border-t border-border/60 pt-3">
                {["contacted", "scheduled", "joined", "closed"].map((s) => (
                  <Button key={s} size="sm" variant={c.status === s ? "default" : "outline"} onClick={() => updateCallStatus(c.id, s)} data-testid={`call-${c.id}-set-${s}`} className="rounded-full text-xs capitalize">{s}</Button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-serif text-2xl text-forest"><Building2 className="mr-1 inline h-5 w-5 text-emerald" /> Member facility memberships</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          After a facility sends <strong>$299 via Zelle to stressfreeaging@gmail.com</strong>, click <b>Activate Membership</b> to mark them active for 365 days.
        </p>
        <div className="mt-5 grid gap-3">
          {facilities.length === 0 && (
            <Card className="rounded-2xl border border-dashed border-border p-6 text-center text-muted-foreground">No member facilities yet.</Card>
          )}
          {facilities.map((f) => {
            const status = f.membership_status || "trial";
            const daysActive = daysUntil(f.membership_expires_at);
            const daysTrial = daysUntil(f.trial_ends_at);
            const isActive = status === "active" && daysActive > 0;
            const isTrial = status === "trial" && daysTrial > 0;
            return (
              <Card key={f.id} data-testid={`admin-facility-${f.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card p-5">
                <div>
                  <h3 className="font-serif text-lg text-forest">{f.name}</h3>
                  <p className="text-xs text-muted-foreground">{f.city}, {f.state} {f.zip} · {f.phone || "no phone"}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    {isActive && <Badge variant="outline" className="rounded-full border-emerald/50 bg-emerald/10 text-emerald">Active · {daysActive} days left</Badge>}
                    {isTrial && <Badge variant="outline" className="rounded-full border-gold/60 bg-gold/10 text-gold">Trial · {daysTrial} days left</Badge>}
                    {!isActive && !isTrial && <Badge variant="outline" className="rounded-full border-destructive/40 bg-destructive/10 text-destructive">Expired / hidden</Badge>}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => activateMembership(f.id)} disabled={activating === f.id} data-testid={`activate-facility-${f.id}`} className="rounded-full bg-primary text-primary-foreground">
                    <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                    {activating === f.id ? "Activating…" : isActive ? "Renew +365 days" : "Activate Membership"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => deleteFacility(f.id, f.name)} data-testid={`delete-facility-${f.id}`} className="rounded-full border-destructive/50 text-destructive hover:bg-destructive/10">
                    Delete
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-serif text-2xl text-forest"><Sparkles className="mr-1 inline h-5 w-5 text-gold" /> Senior placement leads</h2>
        <p className="mt-1 text-sm text-muted-foreground">Every placement request from the public site lands here.</p>
        <div className="mt-5 grid gap-4">
          {loading && <p className="text-muted-foreground">Loading…</p>}
          {!loading && items.length === 0 && (
            <Card className="rounded-2xl border border-dashed border-border p-8 text-center">
              <p className="font-serif text-xl text-forest">No placement leads yet.</p>
            </Card>
          )}
          {items.map((r) => (
            <Card key={r.id} data-testid={`placement-row-${r.id}`} className="rounded-2xl border border-border/60 bg-card p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif text-2xl text-forest">{r.contact_name || r.contact_email}</h3>
                  <p className="text-sm text-muted-foreground">For: <span className="text-foreground">{r.loved_one_name || "—"}</span> · Priority: <span className="text-foreground">{PRIORITY_LABEL[r.care_priority] || r.care_priority || "—"}</span></p>
                  <p className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {TIMELINE_LABEL[r.timeline] || r.timeline || "—"}</span>
                    <span>Area: {r.preferred_area || "—"}</span>
                    <span>Prefers: {r.preferred_contact}</span>
                    {r.referral_source && <span className="rounded-full bg-emerald/10 px-2 py-0.5 text-emerald">via {r.referral_source}</span>}
                  </p>
                </div>
                <Badge variant="outline" className="rounded-full text-xs capitalize">{r.status}</Badge>
              </div>
              {(r.situation || r.message) && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {r.situation && (<div className="rounded-xl bg-muted/40 p-3"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Situation</p><p className="mt-1 text-sm text-foreground">{r.situation}</p></div>)}
                  {r.message && (<div className="rounded-xl bg-muted/40 p-3"><p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes</p><p className="mt-1 text-sm text-foreground">{r.message}</p></div>)}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border/60 pt-4">
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <a href={`mailto:${r.contact_email}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`placement-email-${r.id}`}><Mail className="h-3.5 w-3.5" /> {r.contact_email}</a>
                  {r.contact_phone && (<a href={`tel:${r.contact_phone}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`placement-phone-${r.id}`}><Phone className="h-3.5 w-3.5" /> {r.contact_phone}</a>)}
                </div>
                <div className="flex flex-wrap gap-2">
                  {["contacted", "tour_scheduled", "placed", "closed"].map((s) => (
                    <Button key={s} size="sm" variant={r.status === s ? "default" : "outline"} onClick={() => updateStatus(r.id, s)} data-testid={`placement-${r.id}-set-${s}`} className="rounded-full text-xs capitalize">{s.replace("_", " ")}</Button>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
